<template>
  <div>
    
    <component-wrapper spacing="bottom" v-if="!showSummary">
      <progress-bar 
        id="progress-bar" 
        v-bind:currentStep="currentStep" 
        v-bind:stepOneTitle="content.stepOneTitle"
        v-bind:stepTwoTitle="content.stepTwoTitle"
        v-bind:stepThreeTitle="content.stepThreeTitle"
        v-bind:stepFourTitle="content.stepFourTitle">
      </progress-bar>
    </component-wrapper>
    
    <step-panel v-bind:isVisible="showStepOne">
      <div v-show="showSummary">
        <component-wrapper spacing="bottom">
          <section-header
            v-bind:desc="content.summaryDesc"
            v-bind:title="content.summaryTitle">
          </section-header>
        </component-wrapper>
        <summary-box v-bind:summary="wizard">
          <base-button v-on:click="toggleSummary">Back</base-button>
          <base-button v-on:click="collateAnswers">Continue</base-button>
        </summary-box>
      </div>

      <div v-show="!showSummary">
        <component-wrapper v-if="content.stepOneTitle && content.stepOneDesc" spacing="bottom">
          <section-header
            v-bind:desc="content.stepOneDesc"
            v-bind:title="content.stepOneTitle">
          </section-header>
        </component-wrapper>
        <component-wrapper v-for="(question, index) in wizard" v-bind:key="question.id" v-bind:id="question.id">
          <component
            v-bind:index="index"
            v-bind:is="question.type"
            v-bind:question="question"
            @hook:updated="scrollToComponent(question.id)"
            v-on:input="injectNextQuestion">
          </component>
        </component-wrapper>
        <component-wrapper spacing="bottom" v-if="offerUpload && isFinalQuestion">
          <upload-box
            v-bind:extensions="extensions"
            v-bind:title="content.uploadTitle"
            v-bind:uploadPostAction="uploadPostAction"
            v-on:file-upload="setFileId">
          </upload-box>
        </component-wrapper>
          <component-wrapper
            spacing="bottom"
            v-show="isFinalQuestion">
            <div class="c-rap-actions">
              <base-button
                v-bind:disabled="disableWizardContinue" 
                v-on:click="toggleSummary">
                Continue
              </base-button>
            </div>
          </component-wrapper>
      </div>

    </step-panel>

    <step-panel v-bind:isVisible="showStepTwo">
      <component-wrapper v-if="content.stepTwoTitle && content.stepTwoDesc" spacing="bottom">
          <section-header
            v-bind:desc="content.stepTwoDesc"
            v-bind:title="content.stepTwoTitle">
          </section-header>
        </component-wrapper>
      <component-wrapper spacing="bottom">
        <location-box
          v-bind:contactDetailsRequestError="contactDetailsRequestError"
          v-bind:contactDetailsRequestErrorText="contactDetailsRequestErrorText"
          v-bind:backButtonText="content.backButtonText"
          v-bind:continueButtonText="content.continueButtonText"
          v-bind:componentId="componentId"
          v-bind:customerAccountReferenceLabel="content.customerAccountReferenceLabel"
          v-bind:customerLocationValidationError="content.customerLocationValidationError"
          v-bind:isLoading="isLoading"
          v-bind:locationNotFound="content.locationNotFound"
          v-bind:locationNotListed="content.locationNotListed"
          v-bind:locationNotValid="content.locationNotValid"
          v-bind:locationValid="locationValid"
          v-bind:notFound="notFound"
          v-bind:postcodeLabel="content.postcodeLabel"
          v-bind:suggestedAddresses="suggestedAddresses"
          v-bind:tryAgainButtonText="content.tryAgainButtonText"
          v-on:addressChange="addressChange"
          v-on:locationRequest="getContactDetails"
          v-on:locationReset="resetSuggestedAddresses"
          v-on:stepChange="stepChange"
          v-on:validateCustomer="validateCustomer">
        </location-box>
      </component-wrapper>
    </step-panel>

    <step-panel v-bind:isVisible="showStepThree">

      <div v-show="showSummary">
        <component-wrapper spacing="bottom">
          <section-header
            v-bind:desc="content.summaryDesc"
            v-bind:title="content.summaryTitle">
          </section-header>
        </component-wrapper>

        <summary-box v-bind:isLoading="isLoading" v-bind:summary="details">
          <base-button v-on:click="resetDetails">Back</base-button>
          <base-button v-on:click="postIncidentReport">Continue</base-button>
        </summary-box>
      </div>

      <div v-show="!showSummary">
        <component-wrapper v-if="content.stepThreeTitle && content.stepThreeDesc" spacing="bottom">
          <section-header
            v-bind:desc="content.stepThreeDesc"
            v-bind:title="content.stepThreeTitle">
          </section-header>
        </component-wrapper>
        <component-wrapper spacing="bottom">
          <form-box
            v-bind:aboutYouEmailLabel="content.aboutYouEmailLabel"
            v-bind:aboutYouFirstNameLabel="content.aboutYouFirstNameLabel"
            v-bind:aboutYouOtherTitleLabel="content.aboutYouOtherTitleLabel"
            v-bind:aboutYouPrimaryContactLabel="content.aboutYouPrimaryContactLabel"
            v-bind:aboutYouSecondaryContactLabel="content.aboutYouSecondaryContactLabel"
            v-bind:aboutYouSurnameLabel="content.aboutYouSurnameLabel"
            v-bind:aboutYouTitleLabel="content.aboutYouTitleLabel"
            v-bind:backButtonText="content.backButtonText"
            v-bind:continueButtonText="content.continueButtonText"
            v-bind:email="formBoxData.email"
            v-bind:firstName="formBoxData.firstName"
            v-bind:surname="formBoxData.surname"
            v-bind:otherTitle="formBoxData.otherTitle"
            v-bind:primaryContact="formBoxData.primaryContact"
            v-bind:secondaryContact="formBoxData.secondaryContact"
            v-bind:title="formBoxData.title"
            v-on:collateDetails="collateDetails"
            v-on:stepChange="stepChange"
            v-on:valueChange="formBoxValueChange">
          </form-box>
        </component-wrapper>
      </div>

    </step-panel>

    <step-panel v-bind:isVisible="showStepFour">
      <component-wrapper spacing="bottom">
        <confirmation-box
          v-bind:message="confirmationMessage"
          v-bind:reference="confirmationReference"
          v-bind:title="confirmationTitle"
          v-bind:confirmationCtaUrl="content.stepFourConfirmationCtaUrl"
          v-bind:confirmationCtaText="content.stepFourConfirmationCtaText">
        </confirmation-box>
      </component-wrapper>
    </step-panel>

  </div>
</template>

<script>
import axios from 'axios';
import endpoints from 'Globals/mando.endpoints';
import jump from 'jump.js';

import BaseButton from './BaseButton';
import ComponentWrapper from './ComponentWrapper';
import ConfirmationBox from './ConfirmationBox';
import FormBox from './FormBox';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import LocationBox from './LocationBox';
import ProgressBar from './ProgressBar';
import RadioOptions from './RadioOptions';
import SectionHeader from './SectionHeader';
import StepPanel from './StepPanel';
import SummaryBox from './SummaryBox';
import TextareaBox from './TextareaBox';
import UploadBox from './UploadBox';

export default {
  components: {
    BaseButton,
    ComponentWrapper,
    ConfirmationBox,
    FormBox,
    FormInput,
    FormSelect,
    LocationBox,
    ProgressBar,
    RadioOptions,
    SectionHeader,
    StepPanel,
    SummaryBox,
    TextareaBox,
    UploadBox,
  },
  data() {
    return {
      address: {},
      answers: [],
      details: [],
      confirmationMessage: '',
      confirmationReference: '',
      confirmationTitle: '',
      contactDetailsRequestError: false,
      contactDetailsRequestErrorText: '',
      currentStep: 1,
      locationValid: true,
      extensions: ['jpeg', 'jpe', 'jpg', 'png', 'heic', 'hdr'],
      formBoxData: {
        email: '',
        firstName: '',
        otherTitle: '',
        primaryContact: '',
        secondaryContact: '',
        surname: '',
        title: '',
      },
      fileUploadId: '',
      isLoading: false,
      notFound: false,
      isFinalQuestion: false,
      offerUpload: false,
      showSummary: false,
      suggestedAddresses: [],
      uploadPostAction: '/ytlapi/reportaproblemfileuploadproxy/post',
      wizard: []
    };
  },
  mounted() {
    this.wizard.push(this.questions[0]);
  },
  computed: {
    componentId() {
      return window.WESSEXWATER.componentId;
    },
    content() {
      return window.WESSEXWATER.content;
    },
    currentQuestionIndex() {
      return this.wizard.length - 1;
    },
    disableWizardContinue() {
      const answerCheck = this.wizard.find(v => v.answer === '' && v.mandatory === true);
      return answerCheck;
    },
    questions() {
      return window.WESSEXWATER.questions;
    },
    showStepFour() {
      return this.currentStep === 4;
    },
    showStepOne() {
      return this.currentStep === 1;
    },
    showStepThree() {
      return this.currentStep === 3;
    },
    showStepTwo() {
      return this.currentStep === 2;
    },
    customerOnly() { 
      return this.wizard.find(o => o.customerOnly === true) ? true : false
    },
    uploadEnable() {
      return this.wizard.find(o => o.upload === true) ? true : false
    }
  },
  methods: {
    addressChange(val) {
      this.address = this.suggestedAddresses.find(v => v.value === val);
    },
    collateAnswers(e) {
      this.answers = this.wizard.map(question => {
        return { id: question.id, answer: question.answerId, customerOnly: question.customerOnly, uploadEnable: question.uploadEnable };
      });

      this.stepChange(2, true);
    },
    collateDetails() {
      this.details = [
        {
          answerLabel: this.address.label,
          label: 'Location',
        },
        {
          answerLabel: this.formBoxData.title,
          label: this.content.aboutYouTitleLabel,
        },
        {
          answerLabel: this.formBoxData.otherTitle,
          label: this.content.aboutYouOtherTitleLabel,
        },
        {
          answerLabel: this.formBoxData.firstName,
          label: this.content.aboutYouFirstNameLabel,
        },
        {
          answerLabel: this.formBoxData.surname,
          label: this.content.aboutYouSurnameLabel,
        },
        {
          answerLabel: this.formBoxData.email,
          label: this.content.aboutYouEmailLabel,
        },
        {
          answerLabel: this.formBoxData.primaryContact,
          label: this.content.aboutYouPrimaryContactLabel,
        },
        {
          answerLabel: this.formBoxData.secondaryContact,
          label: this.content.aboutYouSecondaryContactLabel,
        }
      ];
      this.toggleSummary();
    },
    formBoxValueChange(key, value) {
      this.formBoxData[key] = value;
    },
    getContactDetails(componentId, reference, postcode) {
      const queryString = `componentId=${componentId}&` + (reference ? `customerReference=${reference}` : `postcode=${postcode}`);

      this.contactDetailsRequestError = false;
      this.notFound = false;
      this.isLoading = true;

      axios
        .get(`${endpoints('getContactDetails')}?${queryString}`)
        .then(this.getContactDetailsSuccess)
        .catch(this.getContactDetailsFail)
        .finally(this.getContactDetailsFinally);
    },
    getContactDetailsFail() {
      this.contactDetailsRequestError = true;
    },
    getContactDetailsFinally() {
      this.isLoading = false;
    },
    getContactDetailsSuccess(r) {
      if (r.data.results.length) {
        this.suggestedAddresses = r.data.results;
      } else {
        this.notFound = true;
      }

      this.contactDetailsRequestErrorText = r.data.error ? r.data.errorText : '';
    },
    injectNextQuestion(value, questionIndex, questionId, nextId, isFinal, offerUpload) {
      this.isFinalQuestion = isFinal;
      this.offerUpload = offerUpload;
      
      if (questionIndex < this.currentQuestionIndex) {
        this.wizard[questionIndex + 1].answer = '';
        this.wizard[questionIndex + 1].answerLabel = '';
        this.wizard.splice(questionIndex + 1);
      }

      if (nextId) {
        this.wizard.push(this.questions.find(q => q.id === nextId));
      }

      this.wizard[questionIndex].answerId = this.lookupProp(value, questionIndex, 'optionId');
      this.wizard[questionIndex].answerLabel = this.lookupProp(value, questionIndex, 'label');
      this.wizard[questionIndex].customerOnly = this.lookupProp(value, questionIndex, 'customerOnly');
      this.wizard[questionIndex].uploadEnable = this.lookupProp(value, questionIndex, 'upload');

      
    },
    lookupProp(value, questionIndex, prop) {
      if (this.wizard[questionIndex].type === 'radio-options') {
        return this.wizard[questionIndex].options.find(opt => opt.value === value)[prop];
      }
      return value;
    },
    postIncidentReport() {
      this.isLoading = true;

      const payload = {
        "componentId": this.componentId,
        "title": (this.formBoxData.title === 'Other') ? this.formBoxData.otherTitle : this.formBoxData.title,
        "firstName": this.formBoxData.firstName,
        "surname": this.formBoxData.surname,
        "primaryContact": this.formBoxData.primaryContact,
        "secondaryContact": this.formBoxData.secondaryContact,
        "email": this.formBoxData.email,
        "priorityServices": '',
        "addressId": this.address.value,
        "answers": this.answers,
        "fileUploadId": this.fileUploadId,
      };

      axios
        .post(`${endpoints('postIncidentReport')}`, payload)
        .then(this.postIncidentReportSuccess)
        .catch(this.postIncidentReportFail)
        .finally(this.postIncidentReportFinally);
    },
     postIncidentReportFail() {
      this.isLoading = false;
      this.confirmationTitle = 'Submission Error'
      this.confirmationMessage = 'There was an error during submission. Please try again later.';
      this.stepChange(4);
    },
    postIncidentReportFinally() {
      this.isLoading = false;
      this.showSummary = false;
    },
    postIncidentReportSuccess(r) {
      if(!r.data.error) {
        this.confirmationMessage = r.data.results.message;
        this.confirmationReference = r.data.results.reference;
        this.confirmationTitle = r.data.results.title;
      }
      else{
        this.confirmationTitle = 'Submission Error'
        this.confirmationMessage = r.data.errorText;
      }
      this.stepChange(4);
    },
    resetDetails() {
      this.details = [];
      this.showSummary = false;
    },
    resetSuggestedAddresses() {
      this.suggestedAddresses = [];
      this.locationValid = true;
      this.address = {};
    },
    scrollToComponent(id) {
      const el = document.getElementById(id)
      el.scrollIntoView({behavior: 'smooth'});
    },
    stepChange(step, toggleSummary) {
      this.currentStep = step;
      jump('.c-breadcrumb');
      
      if (toggleSummary) {
        this.toggleSummary();
      }
    },
    setFileId(e) {
      this.fileUploadId = e.results.id;
    },
    toggleSummary() {
      this.showSummary = !this.showSummary;
      if (this.showSummary === true) {
        jump('.c-breadcrumb');
      }
    },
    validateCustomer() {
      if((!this.customerOnly) || (this.customerOnly && this.address.wessexProvider)){
        this.locationValid = true;
        this.stepChange(3);
      } else {
        this.locationValid = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/components/consumer/report-a-problem/_vue/ReportProblem.vue